import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import type { LucideIcon } from "lucide-react";

interface AuthGateProps {
  /** Icon that matches the gated page (Ticket, Settings, CalendarClock…). */
  icon: LucideIcon;
  /** Headline in the cinema voice, e.g. "Your seat needs a ticket". */
  title: string;
  /** One or two sentences on what the page actually does once signed in. */
  description: string;
  /** Where "back" leads. Defaults to the home page / foyer. */
  backTo?: string;
  backLabel?: string;
}

/**
 * Themed sign-in gate for member-only routes. Mirrors the Watch Planner gate so
 * signed-out visitors always land on an explanation plus two clear exits —
 * never a blank page or an endless spinner.
 */
export const AuthGate = ({
  icon: Icon,
  title,
  description,
  backTo = "/",
  backLabel = "Back to the foyer",
}: AuthGateProps) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="max-w-md w-full rounded-2xl border border-marquee-bulb/25 bg-card/70 p-8 text-center backdrop-blur">
        <Icon className="h-10 w-10 mx-auto text-marquee-bulb mb-4" />
        <h1 className="cinema-headline text-2xl mb-2">{title}</h1>
        <p className="text-sm text-muted-foreground leading-relaxed mb-6">{description}</p>
        <div className="flex items-center justify-center gap-2">
          <Button onClick={() => navigate("/auth")}>Sign in</Button>
          <Button variant="outline" onClick={() => navigate(backTo)}>{backLabel}</Button>
        </div>
      </div>
    </div>
  );
};
